import React from 'react';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Button from '@atlaskit/button/default/button';
import Textfield from '@atlaskit/textfield/text-field';
import SearchIcon from '@atlaskit/icon/core/search';
import FilterIcon from '@atlaskit/icon/core/filter';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
// eslint-disable-next-line no-restricted-imports, jira/restricted/@atlaskit+dropdown-menu
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link/link';

import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { TypeIdMap, lookupType, type TypeDefinition } from './TypeDefinition.types.tsx';

const allApps = defineMessage({
	id: 'applinks.administration.list.applinks-header.all-apps',
	defaultMessage: 'All Apps',
	description:
		'The text is shown as a button label when no filter is selected in the application links filter dropdown. Used in the filter dropdown trigger button to indicate all apps are displayed.',
});

const applicationLinks = defineMessage({
	id: 'applinks.administration.list.applinks-header.application-links',
	defaultMessage: 'Application Links',
	description:
		'The text is shown as a page header title in the application links administration page. Appears at the top of the page to identify the current section.',
});

const createStepName = defineMessage({
	id: 'applinks.administration.ui.creation-flow-dialog.create-step-name-nonfinal',
	defaultMessage: 'Create link',
	description:
		'The text is shown as a button label when the user wants to create a new application link. Used in the page header to initiate the application link creation flow.',
});

const applicationLinksDescription = defineMessage({
	id: 'applinks.administration.list.applinks-header.application-links-description',
	defaultMessage:
		'Application links let you integrate Jira with another Atlassian product or external application so they can exchange information, resources, and functionalities. What exactly is exchanged will depend on the applications you link.',
	description:
		'The text is shown as a description paragraph explaining what application links are and how they work. Appears below the page header to provide context about application links functionality.',
});

const learnMore = defineMessage({
	id: 'applinks.administration.list.applinks-header.learn-more',
	defaultMessage: 'Learn more about application links',
	description:
		'The text is shown as a link label when the user wants to access additional documentation about application links. Used in the description paragraph to provide a link to support documentation.',
});

const clearSelection = defineMessage({
	id: 'applinks.administration.list.applinks-header.clear-selection',
	defaultMessage: 'Clear selection',
	description:
		'The text is shown as a menu item label when the user wants to clear the current filter selection. Used in the filter dropdown menu to reset the filter and show all application links.',
});
type ApplinksHeaderProps = {
	onCreateLink: () => void;
	onSearch: (query: string) => void;
	onFilterChange: (typeId: string | null) => void;
	filter?: string | null;
	search: string;
};

const styles = cssMap({
	searchBox: {
		flex: '0 0 300px',
	},
});

const ApplinksHeader = (props: ApplinksHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const createFilteredIconButton = ({
		triggerRef,
		...triggerProps
	}: CustomTriggerProps<HTMLButtonElement>) => {
		if (props.filter) {
			const typeDef = lookupType(props.filter);
			return (
				<Button {...triggerProps} iconBefore={FilterIcon} isSelected ref={triggerRef}>
					{typeDef.name}
				</Button>
			);
		}

		return (
			<Button {...triggerProps} iconBefore={FilterIcon} ref={triggerRef}>
				{formatMessage(allApps)}
			</Button>
		);
	};

	return (
		<>
			<Stack space="space.200">
				<Inline spread="space-between">
					<Heading size="large">{formatMessage(applicationLinks)}</Heading>
					<Button appearance="primary" onClick={props.onCreateLink}>
						{formatMessage(createStepName)}
					</Button>
				</Inline>
				<Text>
					{formatMessage(applicationLinksDescription)}{' '}
					<Link href="https://support.atlassian.com/jira-cloud-administration/docs/use-applinks-to-link-to-atlassian-products/">
						{formatMessage(learnMore)}
					</Link>
				</Text>
				<Inline space="space.200" alignBlock="center">
					<Box xcss={styles.searchBox}>
						<Textfield
							placeholder="Search application links"
							elemBeforeInput={<SearchIcon label="Search application links" />}
							onChange={(e) => {
								props.onSearch(e.currentTarget.value);
							}}
						/>
					</Box>
					<DropdownMenu<HTMLButtonElement> trigger={createFilteredIconButton} shouldRenderToParent>
						{props.filter && (
							<DropdownItemGroup>
								<DropdownItem
									key="all"
									elemBefore={<CrossCircleIcon label="Clear filter" />}
									onClick={() => props.onFilterChange(null)}
								>
									{formatMessage(clearSelection)}
								</DropdownItem>
							</DropdownItemGroup>
						)}
						<DropdownItemGroup title="Cloud">
							{Object.entries(TypeIdMap)
								.filter(([key]) => TypeIdMap[key].category === 'cloud')
								.map(([key, typeDef]: [string, TypeDefinition]) => (
									<DropdownItem
										key={key}
										elemBefore={typeDef.icon}
										onClick={() => props.onFilterChange(key)}
									>
										{typeDef.name}
									</DropdownItem>
								))}
						</DropdownItemGroup>
						<DropdownItemGroup title="Data Center">
							{Object.entries(TypeIdMap)
								.filter(([key]) => TypeIdMap[key].category === 'dc')
								.map(([key, typeDef]: [string, TypeDefinition]) => (
									<DropdownItem
										key={key}
										elemBefore={typeDef.icon}
										onClick={() => props.onFilterChange(key)}
									>
										{typeDef.name}
									</DropdownItem>
								))}
						</DropdownItemGroup>
						<DropdownItemGroup title="Miscellaneous">
							<DropdownItem
								key="generic"
								elemBefore={TypeIdMap.generic.icon}
								onClick={() => props.onFilterChange('generic')}
							>
								{TypeIdMap.generic.name}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				</Inline>
			</Stack>
		</>
	);
};

export default ApplinksHeader;
